import React, { useEffect, useRef, useState } from 'react';
import type { BrokerSliceKind, BrokerSliceVinRow, BrokerChipFilterParams } from '../../api/types';
import { BrokerVinTable, type MatchType } from './BrokerVinTable';
import './BrokerSliceView.css';

/** Board filters the shared VIN table scopes to (mirrors the feed). */
export interface SliceBoardParams {
  market_center?: string;
  seller_name?: string;
  auction?: string;
}

interface BrokerSliceViewProps<T> {
  /** Which slice this view renders (drives the VIN↔card join in the table). */
  slice: BrokerSliceKind;
  /** Board filters passed to the shared VIN table. */
  boardParams: SliceBoardParams;
  /** Match-type filter (empty = all), applied server-side in the table. */
  matchTypes?: MatchType[];
  /** Shared chip filters (urgency / days / listing / bids / price), server-side. */
  chipFilters?: BrokerChipFilterParams;
  /** Fetch one page of slice cards. Must honor the abort signal. */
  fetchCards: (
    args: { limit: number; offset: number; signal: AbortSignal },
  ) => Promise<{
    cards: T[];
    total: number;
    matchesWarming?: boolean;
    /** 1-based position of `autoExpandId` in the full ordering, when known. */
    focusRank?: number | null;
  }>;
  /** A cache-busting key that changes when the board filters change (refetch + reset page). */
  fetchKey: string;
  /** The slice key (seller_id / buyer_uid / cluster_key) for a card's VIN table. */
  getSlotKey: (card: T) => string;
  /** Stable React key + expand identity for a card. */
  getCardId: (card: T, index: number) => string;
  /** Slice-specific card header + stats (everything except the VIN drawer). */
  renderCardHeader: (card: T, rank: number, expanded: boolean) => React.ReactNode;
  /** Interactive controls for a card. Rendered as a SIBLING of the head button,
   *  never inside it -- the head is itself a <button>, so nesting one would be
   *  invalid markup and unreachable by keyboard. */
  renderCardActions?: (card: T) => React.ReactNode;
  /** Header/summary line above the card list. */
  renderListHeader?: (from: number, to: number, total: number) => React.ReactNode;
  labels: { loading: string; empty: string; error: string };
  /** Cards per page (card-list pagination; the VIN table paginates independently). */
  limit?: number;
  /** Show the VIN table's best-buyer column (default: seller/cluster only). */
  showBuyer?: boolean;
  onVinOpen?: (vin: string) => void;
  onAddToBasket?: (v: BrokerSliceVinRow, topN: number) => void;
  /** Composite "<buyer_uid>::<vin>" keys already in the basket. */
  basketVins?: Set<string>;
  /** Undo: clicking a staged row's ✓ removes that VIN from the basket. */
  onRemoveFromBasket?: (vin: string) => void;
  /** §10-E fired when a card expands/collapses so the map can focus on it (card or null). */
  onExpandChange?: (card: T | null) => void;
  /** Card id to expand automatically once it appears in the loaded page. */
  autoExpandId?: string | null;
}

/**
 * The unified §11 slice shell: a paginated card list where each card expands to
 * the shared `BrokerVinTable`. One component serves the Buyer and Cluster slices
 * (and could serve Seller) — only the card-header content + the card-list fetch
 * differ per slice, supplied as props. Replaces the bespoke `BoardBuyerLens` /
 * `BoardClusterLens` + their pop-out modals.
 */
export function BrokerSliceView<T>({
  slice,
  boardParams,
  matchTypes,
  chipFilters,
  fetchCards,
  fetchKey,
  getSlotKey,
  getCardId,
  renderCardHeader,
  renderCardActions,
  renderListHeader,
  labels,
  limit = 25,
  showBuyer,
  onVinOpen,
  onAddToBasket,
  basketVins,
  onRemoveFromBasket,
  onExpandChange,
  autoExpandId,
}: BrokerSliceViewProps<T>) {
  const [cards, setCards] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [warming, setWarming] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Reset to the first page whenever the board filters change.
  useEffect(() => { setOffset(0); setExpandedId(null); onExpandChange?.(null); }, [fetchKey, matchTypes]);

  // Open the requested card once it's on the loaded page (e.g. arriving from a
  // VIN pop-out). Silently no-ops when it's on another page.
  useEffect(() => {
    if (!autoExpandId || cards.length === 0) return;
    const idx = cards.findIndex((c, i) => getCardId(c, i) === autoExpandId);
    if (idx < 0) return;
    setExpandedId(autoExpandId);
    onExpandChange?.(cards[idx]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpandId, cards]);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetchCards({ limit, offset, signal: controller.signal });
        if (cancelled) return;
        setCards(res.cards);
        setTotal(res.total);
        setWarming(!!res.matchesWarming);
        // The focused card can sit on any page, so jump to the one holding it
        // rather than only opening it when it happens to load first.
        const rank = res.focusRank;
        if (autoExpandId && rank != null && rank > 0) {
          const target = Math.floor((rank - 1) / limit) * limit;
          if (target !== offset) setOffset(target);
        }
      } catch (err: any) {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        if (cancelled) return;
        setError(labels.error);
        setCards([]);
        setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey, limit, offset, retryTick]);

  // While the buyer-matches precompute is still building (cold start), poll every
  // 15s so the board populates automatically once it's ready — no manual refresh.
  useEffect(() => {
    if (!warming) return;
    const t = setTimeout(() => setRetryTick((n) => n + 1), 15000);
    return () => clearTimeout(t);
  }, [warming, retryTick]);

  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);

  return (
    <div className="bsv-wrap">
      {loading && <div className="bsv-state">{labels.loading}</div>}
      {!loading && error && <div className="bsv-state bsv-state--error">{error}</div>}
      {!loading && !error && cards.length === 0 && warming && (
        <div className="bsv-state bsv-state--warming">
          Preparing buyer recommendations… This one-time build can take a few minutes after a deploy.
          The board will fill in automatically when it's ready.
        </div>
      )}
      {!loading && !error && cards.length === 0 && !warming && <div className="bsv-state">{labels.empty}</div>}
      {!loading && !error && cards.length > 0 && (
        <>
          {renderListHeader && <div className="bsv-header">{renderListHeader(from, to, total)}</div>}
          {cards.map((card, i) => {
            const cardId = getCardId(card, i);
            const expanded = expandedId === cardId;
            return (
              <div key={cardId} className={`bsv-card${expanded ? ' bsv-card--expanded' : ''}`}>
                <div className="bsv-card-top">
                <button
                  type="button"
                  className="bsv-card-head"
                  aria-expanded={expanded}
                  onClick={() => setExpandedId((cur) => {
                    const next = cur === cardId ? null : cardId;
                    onExpandChange?.(next ? card : null);
                    return next;
                  })}
                >
                  <span className="bsv-card-caret" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
                  <span className="bsv-card-headbody">{renderCardHeader(card, offset + i + 1, expanded)}</span>
                </button>
                {renderCardActions && (
                  <div className="bsv-card-actions">{renderCardActions(card)}</div>
                )}
                </div>
                {expanded && (
                  <div className="bsv-card-drawer">
                    <BrokerVinTable
                      slice={slice}
                      slotKey={getSlotKey(card)}
                      boardParams={boardParams}
                      matchTypes={matchTypes}
                      chipFilters={chipFilters}
                      showBuyer={showBuyer}
                      onVinClick={onVinOpen ? (v) => onVinOpen(v.vin) : undefined}
                      onAddToBasket={onAddToBasket}
                      basketVins={basketVins}
                      onRemoveFromBasket={onRemoveFromBasket}
                    />
                  </div>
                )}
              </div>
            );
          })}
          {total > limit && (
            <div className="bv-pagination bv-lens-pager">
              <button className="bv-page-btn" onClick={() => setOffset(0)} disabled={loading || offset === 0} aria-label="First page">«</button>
              <button className="bv-page-btn" onClick={() => setOffset((o) => Math.max(0, o - limit))} disabled={loading || offset === 0} aria-label="Previous page">‹</button>
              <span className="bv-page-indicator">{Math.floor(offset / limit) + 1} / {Math.max(1, Math.ceil(total / limit))}</span>
              <button className="bv-page-btn" onClick={() => setOffset((o) => (o + limit < total ? o + limit : o))} disabled={loading || to >= total} aria-label="Next page">›</button>
              <button className="bv-page-btn" onClick={() => setOffset(Math.max(0, (Math.ceil(total / limit) - 1) * limit))} disabled={loading || to >= total} aria-label="Last page">»</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
